import { CheckCircle2 } from 'lucide-react'
import CTASection from '../components/CTASection'
import SectionHeader from '../components/SectionHeader'
import { ABOUT, MISSION } from '../content/siteContent'

export default function AboutPage() {
  return (
    <main>
      {/* Page hero */}
      <section className="bg-gradient-to-br from-brand-navy to-brand-blue pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} aria-hidden="true" />
        <div className="section-container relative z-10 text-center">
          <p className="text-brand-amber text-xs font-round font-bold tracking-[0.15em] uppercase mb-3">Who We Are</p>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">About Grace Pointe</h1>
          <p className="text-slate-200 text-lg max-w-xl mx-auto">
            A Christ-centered community built on love, trust, and the belief that every child deserves the very best start in life.
          </p>
        </div>
        {/* Wave */}
        <div className="absolute bottom-0 left-0 right-0" aria-hidden="true">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full" preserveAspectRatio="none">
            <path d="M0 60V30C240 0 480 50 720 30C960 10 1200 50 1440 30V60H0Z" fill="#FDF8F0" />
          </svg>
        </div>
      </section>

      {/* Intro */}
      <section className="section-pad bg-brand-ivory">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <p className="text-brand-blue text-xs font-round font-bold tracking-[0.15em] uppercase mb-3">Our Story</p>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-brand-navy mb-5 leading-snug">
                Rooted in the Avon Community
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4">{ABOUT.intro}</p>
              <p className="text-slate-600 leading-relaxed">
                {/* TODO: Add founding story / year if known from owner */}
                We are affiliated with Grace Pointe Church and operate as a ministry to the families of our community, offering superior childcare in a multicultural, Christ-centered environment where every family is welcomed and valued.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4">
            <img src="/images/IMG_7511.jpg" alt="Children learning together" className="rounded-xl3 shadow-card w-full object-cover aspect-square" loading="lazy" />
                <img src="/images/IMG_7541.jpg" alt="Toddler program activities" className="rounded-xl3 shadow-card w-full object-cover aspect-square mt-6" loading="lazy" />
                <img src="/images/IMG_7691.jpg" alt="Preschool activities" className="rounded-xl3 shadow-card w-full object-cover aspect-square" loading="lazy" />
                <img src="/images/IMG_7701.jpg" alt="Happy children at Grace Pointe" className="rounded-xl3 shadow-card w-full object-cover aspect-square mt-6" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="section-pad bg-white">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <SectionHeader eyebrow={MISSION.headline} headline="Our Mission &amp; Vision" />
            <blockquote className="text-lg md:text-xl font-display italic text-brand-navy border-l-4 border-brand-gold pl-5 text-left mb-10 rounded-r-xl bg-brand-cream p-4 sm:p-6">
              {MISSION.quote}
            </blockquote>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {MISSION.pillars.map((p) => (
              <div key={p.title} className="card text-center hover:shadow-lift hover:-translate-y-1 transition-all duration-300">
                <div className="text-3xl mb-3" aria-hidden="true">
                  {p.icon === 'heart' ? '❤️' : p.icon === 'book-open' ? '📖' : p.icon === 'users' ? '👨‍👩‍👧‍👦' : '🌍'}
                </div>
                <h3 className="text-brand-navy font-display font-bold text-lg mb-2">{p.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="section-pad bg-brand-ivory">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <SectionHeader eyebrow="How We Think" headline={ABOUT.philosophy.headline} centered={false} />
              <p className="text-slate-600 leading-relaxed">{ABOUT.philosophy.body}</p>
            </div>
            <div className="relative">
              <img
                src="/images/IMG_7698.jpg"
                alt="Children engaged in learning activities"
                className="rounded-xl3 shadow-lift w-full object-cover aspect-video"
                loading="lazy"
              />
              {/* Floating badge */}
              <div className="absolute -bottom-5 -left-5 bg-brand-gold rounded-xl3 shadow-lift p-5 text-white">
                <p className="font-display font-bold text-lg leading-tight">Christ-Centered</p>
                <p className="text-white/80 text-sm">Values-driven care</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why families choose us */}
      <section className="section-pad bg-white">
        <div className="section-container max-w-4xl">
          <SectionHeader eyebrow="Why Choose Us" headline={ABOUT.whyUs.headline} />
          <ul className="grid sm:grid-cols-2 gap-4">
            {ABOUT.whyUs.points.map((pt) => (
              <li key={pt} className="flex items-start gap-3 bg-brand-ivory rounded-xl2 p-4">
                <CheckCircle2 size={20} className="text-brand-sage flex-shrink-0 mt-0.5" aria-hidden="true" />
                <p className="text-slate-600 text-sm leading-relaxed">{pt}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Team note */}
      <section className="bg-brand-navy section-pad text-center">
        <div className="section-container max-w-2xl">
          <p className="text-brand-amber text-xs font-round font-bold tracking-[0.15em] uppercase mb-3">Our Team</p>
          <h2 className="text-3xl font-display font-bold text-white mb-4">Meet the People Who Make It Happen</h2>
          <p className="text-slate-200 leading-relaxed mb-6">{ABOUT.teamNote}</p>
          {/* TODO: Add staff bios and headshots when provided by owner */}
        </div>
      </section>

      <CTASection />
    </main>
  )
}
